import type { ContactFilterQuery } from "@/features/inbox/types/filter";

import { get as _get, set as _set, isEqual } from "lodash-es";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import {
  InboxSortKey,
  LostConversation,
  SEARCH_FOR,
  SearchFor,
  SORT_OPTION,
} from "@/features/inbox/types/inbox";
import {
  Conversation,
  CONVERSATION_STATUS,
  ConversationStatus,
  ConversationView,
} from "@/types/conversation";
import { FOOTER_INPUT_TYPE, FooterInputType, Message } from "@/types/message";

interface InboxSetting {
  isCustomerPanelOpen: boolean;
  isNoteOpen: boolean;
  isConversationSearchOpen: boolean;
  isMessageSearchOpen: boolean;
  searchText: string;
  sections: Record<string, boolean>;
}

export interface InboxFilter {
  search: {
    $for: SearchFor;
    term: string;
  };
  sort: InboxSortKey;
  view: ConversationView;
  /** A user id, `null` (unassigned), "teammates" (anyone but you), or "all".
   *  `undefined` is the pre-seed default: the URL owns this axis and mirrors
   *  into the store from an effect, which runs after the list has already
   *  mounted - so the list waits rather than fetching everyone's conversations
   *  for a frame and snapping back to Mine. */
  assignedTo: string | null | undefined | "all" | "teammates";
  status: ConversationStatus | "all";
  /** Selected custom inbox. Its rules are resolved from the cached inbox list
   *  and merged into `query`, so this is the only part that hits the URL. */
  customViewId: string | null;
  query: ContactFilterQuery;
  hasActiveFilter: boolean;
}

interface InboxState {
  settings: InboxSetting;
  filters: InboxFilter;
  selectedContactIds: string[];
  selectAllCapped: boolean;
  toggleContactSelection: (id: string) => void;
  setSelectedContactIds: (ids: string[], capped?: boolean) => void;
  clearContactSelection: () => void;
  activeConversation: Conversation | null;
  setActiveConversation: (conversation: Conversation | null) => void;
  updateActiveConversation: (id: string, conversation: Conversation) => void;
  updateSettings: (key: keyof InboxSetting, value: string | boolean) => void;
  updateSection: (key: string, isExpanded: boolean) => void;
  updateFilters: (obj: Record<string, any>) => void;
  selectedReplyToMessage: Message | null;
  setSelectedReplyToMessage: (message: Message | null) => void;
  footerInputType: FooterInputType;
  setFooterInputType: (type: FooterInputType) => void;
  commentReplyMessage: Message | null;
  setCommentReplyMessage: (message: Message | null) => void;
  dmReplyMessage: Message | null;
  setDmReplyMessage: (message: Message | null) => void;
  isConversationAtBottom: boolean;
  setIsConversationAtBottom: (value: boolean) => void;
  scrollToConversationBottom: (() => void) | null;
  setScrollToConversationBottom: (fn: (() => void) | null) => void;
  /** Scrolls the thread to a message's row when it isn't mounted; false when
   *  the open thread doesn't hold that message. */
  revealMessage: ((messageId: string) => boolean) | null;
  setRevealMessage: (fn: ((messageId: string) => boolean) | null) => void;
  /** Unsent reply drafts kept per contact so switching conversations doesn't
   *  lose in-progress text. Memory-only (cleared on full reload). */
  draftsByContactId: Record<string, string>;
  setDraft: (contactId: string, text: string) => void;
  lostConversation: LostConversation | null;
  /** Records the loss and closes the conversation if it is the open one. A loss already
   *  recorded for the contact keeps the name it has when this one brings none. */
  loseConversation: (lost: LostConversation) => void;
  clearLostConversation: () => void;
}

export const defaultFilters: InboxFilter = {
  search: { $for: SEARCH_FOR.CONTACT, term: "" },
  sort: SORT_OPTION.NEWEST,
  view: "all",
  status: CONVERSATION_STATUS.OPEN,
  hasActiveFilter: false,
  assignedTo: undefined,
  customViewId: null,
  query: { rules: [] },
};

export const defaultSettings: InboxSetting = {
  isCustomerPanelOpen: true,
  isNoteOpen: false,
  isConversationSearchOpen: false,
  isMessageSearchOpen: false,
  searchText: "",
  sections: {},
};

export const useInboxStore = create<InboxState>()(
  immer((set, get) => ({
    settings: defaultSettings,
    filters: defaultFilters,
    updateFilters(updates: Record<string, any>) {
      set((state) => {
        let changed = false;

        Object.entries(updates).forEach(([path, value]) => {
          if (!isEqual(_get(state.filters, path), value)) changed = true;
          _set(state.filters, path, value);
        });

        const { hasActiveFilter, ...currentWithoutFlag } = state.filters;
        const { hasActiveFilter: _, ...defaultWithoutFlag } = defaultFilters;

        const isDefault = isEqual(currentWithoutFlag, defaultWithoutFlag);

        state.filters.hasActiveFilter = !isDefault;

        if (changed) {
          state.selectedContactIds = [];
          state.selectAllCapped = false;
        }
      });
    },
    selectedContactIds: [],
    selectAllCapped: false,
    toggleContactSelection(id) {
      set((state) => {
        const index = state.selectedContactIds.indexOf(id);

        if (index === -1) state.selectedContactIds.push(id);
        else state.selectedContactIds.splice(index, 1);

        state.selectAllCapped = false;
      });
    },
    setSelectedContactIds(ids, capped = false) {
      set((state) => {
        state.selectedContactIds = ids;
        state.selectAllCapped = capped;
      });
    },
    clearContactSelection() {
      set((state) => {
        state.selectedContactIds = [];
        state.selectAllCapped = false;
      });
    },
    activeConversation: null,
    updateActiveConversation(id, updates) {
      const state = get();
      const shouldUpdate = state.activeConversation?.contact.id === id;

      set((state) => {
        if (shouldUpdate && state.activeConversation) {
          state.activeConversation = {
            ...state.activeConversation,
            ...updates,
            contact: {
              ...state.activeConversation.contact,
              ...updates.contact,
              ...(updates.contact?.dynamicValues && {
                dynamicValues: {
                  ...state.activeConversation.contact.dynamicValues,
                  ...updates.contact.dynamicValues,
                },
              }),
            },
          };
        }
      });
    },
    setActiveConversation(conversation) {
      set((state) => ({
        ...state,
        activeConversation: conversation,
      }));
    },
    updateSettings(key: keyof InboxSetting, value: string | boolean) {
      set((state) => ({
        ...state,
        settings: {
          ...state.settings,
          [key]: value,
        },
      }));
    },
    updateSection(key: string, isExpanded: boolean) {
      set((state) => {
        state.settings.sections[key] = isExpanded;
      });
    },
    selectedReplyToMessage: null,
    setSelectedReplyToMessage(message) {
      set((state) => ({
        ...state,
        selectedReplyToMessage: message,
      }));
    },
    footerInputType: FOOTER_INPUT_TYPE.MESSAGE,
    setFooterInputType(type) {
      set((state) => ({
        ...state,
        footerInputType: type,
      }));
    },
    commentReplyMessage: null,
    setCommentReplyMessage(message) {
      set((state) => ({
        ...state,
        commentReplyMessage: message,
      }));
    },
    dmReplyMessage: null,
    setDmReplyMessage(message) {
      set((state) => ({
        ...state,
        dmReplyMessage: message,
      }));
    },
    isConversationAtBottom: true,
    setIsConversationAtBottom(value) {
      // Called on every scroll event; a no-op write still wakes every subscriber.
      if (get().isConversationAtBottom === value) return;
      set((state) => ({
        ...state,
        isConversationAtBottom: value,
      }));
    },
    scrollToConversationBottom: null,
    setScrollToConversationBottom(fn) {
      set((state) => ({
        ...state,
        scrollToConversationBottom: fn,
      }));
    },
    revealMessage: null,
    setRevealMessage(fn) {
      set((state) => ({
        ...state,
        revealMessage: fn,
      }));
    },
    draftsByContactId: {},
    setDraft(contactId, text) {
      set((state) => {
        state.draftsByContactId[contactId] = text;
      });
    },
    lostConversation: null,
    loseConversation({ contactId, heldByUserId }) {
      set((state) => {
        const known =
          state.lostConversation?.contactId === contactId
            ? state.lostConversation.heldByUserId
            : null;

        state.lostConversation = {
          contactId,
          heldByUserId: heldByUserId ?? known,
        };
        if (state.activeConversation?.contact.id === contactId)
          state.activeConversation = null;
      });
    },
    clearLostConversation() {
      set((state) => {
        state.lostConversation = null;
      });
    },
  })),
);
